import type { ReactNode } from 'react';
import { Dropdown } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { SupplierRequestType } from '@econtractor/types';
import SvgTrash from 'components/svg/SvgTrash';
import { SUPPLIER_REQUEST_STATUS } from './model/constants';
import useRequestStatusActions from './model/useRequestStatusActions';
import SolarIcon from 'components/icons/SolarIcon';

interface RequestActionsProps {
    row: SupplierRequestType;
    canOps: boolean;
    onOpen: (requestId: number) => void;
    onClone: (row: SupplierRequestType) => void;
    onChanged: () => void;
    onSendEmail: (row: SupplierRequestType) => void;
}

const parseSentEmails = (value?: string): string[] =>
    String(value || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);

const RequestActions = ({
    row,
    canOps,
    onOpen,
    onClone,
    onChanged,
    onSendEmail,
}: RequestActionsProps) => {
    const { t } = useTranslation(['Vendors']);
    const { confirmMarkDone, confirmMarkDeleted } = useRequestStatusActions({ onChanged });

    const status = Number(row.status);
    const requestId = Number(row.ID);
    const vendorName = String(row.vendorName || '').trim() || `#${row.vendorId}`;
    const fileUrl = String(row.filePublicUrl || '').trim();
    const isDeleted = status === SUPPLIER_REQUEST_STATUS.DELETED;
    const canClone = canOps;
    const canMarkDone = canOps && status === SUPPLIER_REQUEST_STATUS.ACTIVE;
    const canDelete =
        canOps &&
        (status === SUPPLIER_REQUEST_STATUS.ACTIVE || status === SUPPLIER_REQUEST_STATUS.DONE);
    const canOpenPdf = Boolean(fileUrl);
    const canSendEmail = canOps && !isDeleted && Boolean(fileUrl);
    const sentEmails = parseSentEmails(row.sentEmail);
    const sendEmailHint = row.sentToEmail
        ? sentEmails.length
            ? sentEmails.join(', ')
            : t('supplierRequests.logSentNoEmail')
        : t('supplierRequests.actions.sendEmailHint');

    const renderMenuItem = (
        icon: ReactNode,
        title: string,
        subtitle: string,
        onSelect: () => void,
        options?: { danger?: boolean; disabled?: boolean },
    ) => (
        <Dropdown.Item
            as="button"
            disabled={options?.disabled}
            className={
                [
                    options?.disabled ? 'dropdown-item-delete-disabled text-body-secondary' : '',
                    options?.danger && !options?.disabled ? 'text-danger' : '',
                ]
                    .filter(Boolean)
                    .join(' ') || undefined
            }
            onClick={(e) => {
                if (options?.disabled) return;
                onSelect();
                e.currentTarget.blur();
            }}
        >
            <div className="d-flex align-items-start w-100 text-start">
                <span
                    className={`d-inline-flex align-items-center justify-content-center flex-shrink-0 me-2 item-icon${
                        options?.disabled
                            ? ' text-body-secondary'
                            : options?.danger
                              ? ' text-danger'
                              : ''
                    }`}
                >
                    {icon}
                </span>
                <div className="flex-grow-1 min-w-0">
                    <div
                        className={`fw-medium ${
                            options?.disabled
                                ? 'text-body-secondary'
                                : options?.danger
                                  ? 'text-danger'
                                  : ''
                        }`}
                    >
                        {title}
                    </div>
                    <small
                        className={`warehouse-team-menu-hint ${
                            options?.disabled ? 'text-body-secondary' : 'text-muted'
                        }`}
                    >
                        {subtitle}
                    </small>
                </div>
            </div>
        </Dropdown.Item>
    );

    const menu = (
        <Dropdown.Menu
            className="p-2 shadow-sm warehouse-team-menu"
            popperConfig={{ strategy: 'fixed' }}
            renderOnMount
        >
            {renderMenuItem(
                <SolarIcon name="eye" />,
                t('supplierRequests.actions.open'),
                t('supplierRequests.actions.openHint'),
                () => onOpen(requestId),
            )}
            {canClone
                ? renderMenuItem(
                      <SolarIcon name="copy" />,
                      t('supplierRequests.actions.clone'),
                      t('supplierRequests.actions.cloneHint'),
                      () => onClone(row),
                  )
                : null}
            <Dropdown.Divider className="my-2" />
            {renderMenuItem(
                <SolarIcon name="file-earmark-pdf" />,
                t('supplierRequests.actions.openPdf'),
                t('supplierRequests.actions.openPdfHint'),
                () => {
                    window.open(fileUrl, '_blank', 'noopener,noreferrer');
                },
                { disabled: !canOpenPdf },
            )}
            {renderMenuItem(
                <SolarIcon name="envelope" />,
                t('supplierRequests.actions.sendEmail'),
                sendEmailHint,
                () => onSendEmail(row),
                { disabled: !canSendEmail },
            )}
            {canMarkDone || canDelete ? <Dropdown.Divider className="my-2" /> : null}
            {canMarkDone
                ? renderMenuItem(
                      <SolarIcon name="check2-circle" />,
                      t('supplierRequests.actions.done'),
                      t('supplierRequests.actions.doneHint'),
                      () => confirmMarkDone(requestId, vendorName),
                  )
                : null}
            {canMarkDone && canDelete ? <Dropdown.Divider className="my-2" /> : null}
            {canDelete
                ? renderMenuItem(
                      <SvgTrash />,
                      t('supplierRequests.actions.delete'),
                      t('supplierRequests.actions.deleteHint'),
                      () => confirmMarkDeleted(requestId, vendorName),
                      { danger: true },
                  )
                : null}
        </Dropdown.Menu>
    );

    return (
        <Dropdown align="end">
            <Dropdown.Toggle
                className="p-1 py-0"
                size="sm"
                variant="light-secondary"
                id={`dropdown-supplier-request-${requestId}`}
            >
                <SolarIcon name="three-dots" />
            </Dropdown.Toggle>
            {typeof document !== 'undefined' ? createPortal(menu, document.body) : menu}
        </Dropdown>
    );
};

export default RequestActions;
